/** What the attention strip lists: the things that go wrong without anyone
 *  looking (plan 1-3). Built from the tracker's status, the analytics
 *  collector's status, the Play slot summary and the last run of each
 *  scheduled job — every one an endpoint that already exists. */

import type { AnalyticsAppSummary, AnalyticsStatus, AttentionDismissal, JobRun, PlaySlotsSummary, TrackingStatus } from '@/api/client'
import { jobLabel } from './status'
import { trackerView } from './tracker'

export type AttentionTone = 'failed' | 'publishing' | 'neutral'

export interface AttentionDetail {
  /** Where it happened: the job, the service. */
  where: string
  /** What it was: a count, a time. */
  what?: string
  /** The job's or the service's own words, whole. */
  message?: string
  at?: string
}

export interface AttentionItem {
  key: string
  tone: AttentionTone
  label: string
  count: number
  to: string
  details: AttentionDetail[]
  /** Which occurrence this is (a run id, a failure's time), for clearing it
   *  by hand: a cleared item stays hidden until its stamp changes, so the
   *  next failure shows again. Absent, the item cannot be cleared — it goes
   *  away by itself once the thing works. */
  stamp?: string
}

export interface AttentionInput {
  tracking?: TrackingStatus | null
  analytics?: AnalyticsStatus | null
  playSlots?: PlaySlotsSummary | null
  /** Recent job_runs rows, newest first. */
  runs?: JobRun[] | null
  now?: Date
}

/** The scheduled jobs whose last failure is worth a line (the tracker has its
 *  own item from its live status). */
export const WATCHED_JOBS = ['asc_keyword_sync', 'store_rank_snapshot', 'apple_popularity', 'analytics_pull', 'playslots_run', 'analytics_purge'] as const

/** A Play slot attempt ending in one of these is a failure; `unverified` is
 *  not (the create may well have gone through). */
const FAILED_ATTEMPT = new Set(['limit', 'login_required', 'error'])

const ATTEMPT_LABEL: Record<string, string> = { limit: '한도', login_required: '로그인 필요', error: '오류' }

const HOUR_MS = 3_600_000

function unixToIso(sec: number | null | undefined): string | undefined {
  return sec && sec > 0 ? new Date(sec * 1000).toISOString() : undefined
}

export function attentionItems(input: AttentionInput): AttentionItem[] {
  const items: AttentionItem[] = []
  const now = input.now ?? new Date()
  const t = input.tracking

  // The tracker keeps its last result in memory, so after a restart its own
  // answer is empty until the first run; the newest snapshot run in the job
  // history stands in for it (trackerView). With neither, both checks wait.
  const view = trackerView(t, input.runs)
  const lastAt = view.lastAt ? new Date(view.lastAt).getTime() : NaN
  if (view.key === 'error' && !Number.isNaN(lastAt)) {
    items.push({
      key: 'tracking-failed',
      tone: 'failed',
      label: '순위 스냅샷 실패',
      count: 0,
      to: '/jobs?job=tracking_snapshot',
      details: [{ where: jobLabel('tracking_snapshot'), message: view.message || '사유가 기록되지 않았다', at: view.lastAt ?? undefined }],
      stamp: view.lastAt ?? '',
    })
  }
  const interval = t?.interval ?? 0
  if (t && !t.running && !Number.isNaN(lastAt) && interval > 0 && now.getTime() - lastAt > interval * 2 * HOUR_MS) {
    const hours = Math.floor((now.getTime() - lastAt) / HOUR_MS)
    items.push({
      key: 'tracking-late',
      tone: 'publishing',
      label: '순위 스냅샷 지연',
      count: 0,
      to: '/settings?tab=tracking',
      details: [{ where: jobLabel('tracking_snapshot'), what: `${hours}시간째 실행 없음 · 주기 ${interval}시간`, at: view.lastAt ?? undefined }],
      stamp: view.lastAt ?? '',
    })
  }

  const a = input.analytics
  const collectorFailed = !!(a && a.configured && !a.last_ok && a.last_error)
  if (collectorFailed) {
    items.push({
      key: 'analytics-failed',
      tone: 'failed',
      label: '자체 분석 수집 실패',
      count: 0,
      to: '/analytics',
      details: [{ where: jobLabel('analytics_pull'), message: a!.last_error, at: a!.last_run_at ?? unixToIso(a!.last_pull_at) }],
      stamp: a!.last_run_at ?? String(a!.last_pull_at),
    })
  }

  const ps = input.playSlots
  if (ps?.blocked_reason) {
    const next = unixToIso(ps.next_allowed_at)
    items.push({
      key: 'playslots-blocked',
      tone: 'publishing',
      label: 'Play 슬롯 잠금',
      count: 0,
      to: '/playslots',
      details: [{ where: 'Play 슬롯', what: next ? '다음 허용 시각' : undefined, message: ps.blocked_reason, at: next }],
      stamp: `${ps.blocked_reason} ${ps.next_allowed_at ?? ''}`,
    })
  }
  const last = ps?.last_attempt
  const attemptFailed = !!(last && FAILED_ATTEMPT.has(last.result))
  if (attemptFailed) {
    items.push({
      key: 'playslots-attempt',
      tone: 'failed',
      label: 'Play 슬롯 마지막 시도 실패',
      count: 0,
      to: '/playslots',
      details: [
        {
          where: last!.package_name || 'Play 슬롯',
          what: ATTEMPT_LABEL[last!.result] ?? last!.result,
          message: last!.message || undefined,
          at: unixToIso(last!.at),
        },
      ],
      stamp: String(last!.id),
    })
  }

  // The newest row per job. A job already reported above is skipped, so one
  // failure is said once.
  const said = new Set<string>()
  if (collectorFailed) said.add('analytics_pull')
  if (attemptFailed) said.add('playslots_run')
  const latest = new Map<string, JobRun>()
  for (const r of input.runs ?? []) {
    if (r.status === 'running') continue
    const prev = latest.get(r.job)
    if (!prev || r.started_at > prev.started_at) latest.set(r.job, r)
  }
  for (const job of WATCHED_JOBS) {
    const r = latest.get(job)
    if (!r || r.status !== 'error' || said.has(job)) continue
    items.push({
      key: `job:${job}`,
      tone: 'failed',
      label: `${jobLabel(job)} 실패`,
      count: 0,
      to: `/jobs?job=${encodeURIComponent(job)}`,
      details: [{ where: jobLabel(job), message: r.message || '사유가 기록되지 않았다', at: r.finished_at ?? r.started_at }],
      stamp: String(r.id),
    })
  }

  return items
}

/** The lines only 오늘's "확인할 것" list shows: nothing is broken, but
 *  something is not set up. Not alarms, so the strip leaves them out. */
export function setupNotes(input: {
  analytics?: AnalyticsStatus | null
  analyticsApps?: AnalyticsAppSummary[] | null
}): AttentionItem[] {
  const notes: AttentionItem[] = []
  if (input.analytics && !input.analytics.configured) {
    notes.push({
      key: 'analytics-unconfigured',
      tone: 'neutral',
      label: '자체 분석 수집기 미설정',
      count: 0,
      to: '/settings?tab=analytics',
      details: [{ where: '설정', what: '수집기 주소와 토큰을 넣으면 사용량이 쌓인다' }],
      stamp: 'unconfigured',
    })
  }
  const unlinked = (input.analyticsApps ?? []).filter((a) => !a.linked)
  if (unlinked.length > 0) {
    notes.push({
      key: 'analytics-unlinked',
      tone: 'neutral',
      label: 'ASO 앱과 연결 안 된 텔레메트리',
      count: unlinked.length,
      to: '/analytics',
      details: unlinked.map((a) => ({ where: a.app_id, what: a.platforms || undefined })),
      // a newly unlinked app brings it back
      stamp: unlinked.map((a) => a.app_id).sort().join(' '),
    })
  }
  return notes
}

/** The items left once those cleared by hand are taken out. An item is cleared
 *  while its stamp is the one cleared; a new occurrence shows again. */
export function withoutDismissed(items: AttentionItem[], dismissals?: AttentionDismissal[] | null): AttentionItem[] {
  if (!dismissals?.length) return items
  const cleared = new Map(dismissals.map((d) => [d.key, d.stamp]))
  return items.filter((i) => i.stamp === undefined || cleared.get(i.key) !== i.stamp)
}
